import React, { useEffect } from 'react';
import { Box, Typography, Container, Button, Grid } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO.tsx';

const GradientText = styled('span')({
  background: 'linear-gradient(45deg, #6366F1 30%, #FFC107 90%)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
});

const AnimatedBox = styled(Box)({
  animation: 'fadeIn 1s ease-in',
  '@keyframes fadeIn': {
    '0%': {
      opacity: 0,
      transform: 'translateY(20px)',
    },
    '100%': {
      opacity: 1,
      transform: 'translateY(0)',
    },
  },
});

const Home = () => {
  useEffect(() => {
    document.title = 'Alexandre Mami | Home';
  }, []);

  return (
    <>
      <SEO
        title="Home"
        description="Alexandre Mami - Full Stack Developer spécialisé dans le développement d'applications web modernes et performantes avec Java, Spring et React."
        keywords="développeur web, full stack, java, spring, react, portfolio, alexandre mami, développeur freelance"
        ogImage="https://mamialex.ru/images/seo-preview.jpg"
        ogType="website"
      />
      <Box
        sx={{
          minHeight: '100%',
          display: 'flex',
          alignItems: 'center',
          background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 100%)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Background Animation */}
        <Box
          sx={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            background: 'radial-gradient(circle at center, rgba(99, 102, 241, 0.15) 0%, transparent 70%)',
            animation: 'pulse 15s infinite',
            '@keyframes pulse': {
              '0%': { opacity: 0.5 },
              '50%': { opacity: 0.8 },
              '100%': { opacity: 0.5 },
            },
          }}
        />

        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={8}>
              <AnimatedBox>
                <Typography variant="h2" gutterBottom sx={{ fontWeight: 700 }}>
                  Hello, I'm <GradientText>Alexandre Mami</GradientText>
                </Typography>
                <Typography variant="h4" gutterBottom sx={{ color: 'text.secondary', mb: 4 }}>
                  Developer
                </Typography>
                <Typography variant="body1" paragraph sx={{ color: 'text.secondary', fontSize: '1.2rem', maxWidth: '600px' }}>
                I specialize in building modern, high-performance web applications and turning ideas into innovative digital solutions.
                </Typography>
                <Box sx={{ mt: 4, display: 'flex', gap: 2 }}>
                  <Button
                    component={Link}
                    to="/portfolio"
                    variant="contained"
                    size="large"
                    sx={{
                      background: 'linear-gradient(45deg, #6366F1 30%, #FFC107 90%)',
                      color: 'white',
                      px: 4,
                      '&:hover': {
                        background: 'linear-gradient(45deg, #FFC107 30%, #6366F1 90%)',
                      },
                    }}
                  >
                    View my portfolio
                  </Button>
                  <Button
                    component={Link}
                    to="/contact"
                    variant="outlined"
                    size="large"
                    sx={{
                      borderColor: '#6366F1',
                      color: '#6366F1',
                      px: 4,
                      '&:hover': {
                        borderColor: '#FFC107',
                        color: '#FFC107',
                      },
                    }}
                  >
                    Contact me
                  </Button>
                </Box>
              </AnimatedBox>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box
                sx={{
                  position: 'relative',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: '-20px',
                    right: '-20px',
                    width: '100%',
                    height: '100%',
                    border: '2px solid',
                    borderImage: 'linear-gradient(45deg, #6366F1, #FFC107) 1',
                    zIndex: 1,
                  },
                }}
              >
                <Box
                  component="img"
                  src="/images/profile.png"
                  alt="Alexandre Mami"
                  sx={{
                    width: '100%',
                    height: 'auto',
                    borderRadius: '12px',
                    position: 'relative',
                    zIndex: 2,
                    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.2)',
                    transition: 'all 0.3s ease-in-out',
                    backgroundColor: 'transparent',
                    objectFit: 'contain',
                    filter: 'brightness(1.02)',
                    '&:hover': {
                      transform: 'scale(1.02)',
                      filter: 'brightness(1.05)',
                    },
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </>
  );
};

export default Home; 